/**
 * Mutation callbacks for `CommentsProvider` (#2244).
 *
 * Lives next to `comments-context.tsx` so the provider stays under
 * the 300-line budget the ticket's DoD asks for. Pure wiring: wraps
 * the three CRUD + two resolve react-query mutations and surfaces
 * them as stable `async` callbacks that the context value forwards.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import type { Comment } from '@utils/types/comments/comments.ts';
import { useCreateComment } from '@utils/api/comments/use-create-comment.tsx';
import { useUpdateComment } from '@utils/api/comments/use-update-comment.tsx';
import { useDeleteComment } from '@utils/api/comments/use-delete-comment.tsx';
import {
  useResolveComment,
  useUnresolveComment
} from '@utils/api/comments/use-resolve-comment.tsx';
import { errorMessage } from './comments-selectors.ts';

export interface CommentMutations {
  createOne: (payload: {
    text: string;
    indexFrom: string;
    indexTo: string;
  }) => Promise<void>;
  updateOne: (
    id: number,
    text: string,
    indexFrom: string,
    indexTo: string
  ) => Promise<void>;
  deleteOne: (id: number) => Promise<void>;
  toggleResolved: (comment: Comment) => Promise<void>;
}

export function useCommentMutations(
  sessionId: string,
  documentId: string
): CommentMutations {
  const { t } = useTranslation();
  const { mutateAsync: createComment } = useCreateComment();
  const { mutateAsync: updateComment } = useUpdateComment();
  const { mutateAsync: deleteComment } = useDeleteComment();
  const { mutateAsync: resolveComment } = useResolveComment();
  const { mutateAsync: unresolveComment } = useUnresolveComment();

  const createOne = useCallback(
    async ({
      text,
      indexFrom,
      indexTo
    }: {
      text: string;
      indexFrom: string;
      indexTo: string;
    }) => {
      try {
        await createComment({
          session_id: sessionId,
          document_id: documentId,
          text,
          index_from: indexFrom,
          index_to: indexTo
        });
        toast.success(t('comment-created-successfully'));
      } catch (err) {
        throw new Error(errorMessage(err, t('an-error-occurred')));
      }
    },
    [createComment, sessionId, documentId, t]
  );

  const updateOne = useCallback(
    async (id: number, text: string, indexFrom: string, indexTo: string) => {
      try {
        await updateComment({
          session_id: sessionId,
          document_id: documentId,
          comment_id: id.toString(),
          payload: {
            text,
            index_from: indexFrom || '00',
            index_to: indexTo || '00'
          }
        });
        toast.success(t('comment-updated=successfully'));
      } catch (err) {
        throw new Error(errorMessage(err, t('an-error-occurred')));
      }
    },
    [updateComment, sessionId, documentId, t]
  );

  const deleteOne = useCallback(
    async (id: number) => {
      try {
        await deleteComment({
          session_id: sessionId,
          document_id: documentId,
          comment_id: id.toString()
        });
        toast.success(t('comment-deleted-successfully'));
      } catch (err) {
        toast.error(errorMessage(err, t('an-error-occurred')));
      }
    },
    [deleteComment, sessionId, documentId, t]
  );

  const toggleResolved = useCallback(
    async (c: Comment) => {
      try {
        if (c.resolved) {
          await unresolveComment({
            session_id: sessionId,
            document_id: documentId,
            comment_id: c.id.toString()
          });
          toast.success(t('comment-unresolved'));
        } else {
          await resolveComment({
            session_id: sessionId,
            document_id: documentId,
            comment_id: c.id.toString()
          });
          toast.success(t('comment-resolved'));
        }
      } catch (err) {
        toast.error(errorMessage(err, t('an-error-occurred')));
      }
    },
    [resolveComment, unresolveComment, sessionId, documentId, t]
  );

  return { createOne, updateOne, deleteOne, toggleResolved };
}
